import { useCallback, useEffect, useRef, useState } from 'react';

export type PageNotificationTone = 'ok' | 'warn' | 'error' | 'neutral' | 'info';

export type PageNotification = {
  id: string;
  tone: PageNotificationTone;
  title: string;
  lines: string[];
  createdAt: string;
  durationMs?: number;
};

export type PushPageNotificationInput = {
  tone?: PageNotificationTone;
  title: string;
  lines?: string[];
  durationMs?: number;
};

const DEFAULT_TOAST_MS = 4000;
const ERROR_TOAST_MS = 9000;
const MAX_HISTORY = 50;

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function usePageNotifications() {
  const [items, setItems] = useState<PageNotification[]>([]);
  const [activeToast, setActiveToast] = useState<PageNotification | null>(null);
  const queueRef = useRef<PageNotification[]>([]);
  const timerRef = useRef<number | null>(null);
  const showingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showNext = useCallback(() => {
    const next = queueRef.current.shift() ?? null;
    if (!next) {
      showingRef.current = false;
      setActiveToast(null);
      return;
    }
    showingRef.current = true;
    setActiveToast(next);
    clearTimer();
    const durationMs = next.durationMs ?? DEFAULT_TOAST_MS;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      showNext();
    }, durationMs);
  }, [clearTimer]);

  const dismissToast = useCallback(() => {
    clearTimer();
    showNext();
  }, [clearTimer, showNext]);

  const push = useCallback(
    (input: PushPageNotificationInput) => {
      const notification: PageNotification = {
        id: makeId(),
        tone: input.tone ?? 'info',
        title: input.title.trim(),
        lines: (input.lines ?? []).map((line) => line.trim()).filter(Boolean),
        createdAt: new Date().toISOString(),
        durationMs:
          input.durationMs ??
          (input.tone === 'warn' || input.tone === 'error' ? ERROR_TOAST_MS : DEFAULT_TOAST_MS),
      };
      if (!notification.title) return;

      setItems((prev) => [notification, ...prev].slice(0, MAX_HISTORY));
      queueRef.current.push(notification);
      if (!showingRef.current) {
        showNext();
      }
    },
    [showNext],
  );

  const clearAll = useCallback(() => {
    queueRef.current = [];
    clearTimer();
    showingRef.current = false;
    setItems([]);
    setActiveToast(null);
  }, [clearTimer]);

  useEffect(
    () => () => {
      clearTimer();
    },
    [clearTimer],
  );

  return {
    items,
    activeToast,
    push,
    dismissToast,
    clearAll,
  };
}
